import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, X, Send, Bot, User, Loader2, Minimize2, Maximize2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AiChatPanel() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Здравейте! Аз съм AI асистентът на GreenCastle. Как мога да ви помогна днес?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      // Send only user/assistant messages (not system)
      const chatHistory = newMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .slice(-10); // Last 10 messages for context

      const response = await axios.post(`${API}/ai/chat`, { messages: chatHistory });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.result }]);
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Грешка при свързване с AI';
      setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${errMsg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      { role: 'assistant', content: 'Чатът е изчистен. Как мога да ви помогна?' }
    ]);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#1B5E37] hover:bg-[#154a2c] text-white rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-105"
        data-testid="ai-chat-toggle"
        title="AI Асистент"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div 
      className={`fixed z-50 bg-white rounded-2xl shadow-2xl border border-border flex flex-col transition-all duration-300 ${
        minimized 
          ? 'bottom-6 right-6 w-72 h-14' 
          : 'bottom-6 right-6 w-[420px] h-[600px] max-h-[80vh]'
      }`}
      data-testid="ai-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#1B5E37] text-white rounded-t-2xl flex-shrink-0">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" />
          <span className="font-semibold text-sm">AI Асистент</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMinimized(!minimized)} className="p-1 hover:bg-white/20 rounded transition-colors">
            {minimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!minimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 bg-[#1B5E37]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot className="w-4 h-4 text-[#1B5E37]" />
                  </div>
                )}
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user' 
                    ? 'bg-[#1B5E37] text-white rounded-br-md' 
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}>
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3 items-start">
                <div className="w-7 h-7 bg-[#1B5E37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-[#1B5E37]" />
                </div>
                <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
                  <Loader2 className="w-4 h-4 animate-spin text-[#1B5E37]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick actions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {[
                'Генерирай описание на имот',
                'Подобри маркетинг текст',
                'Съвети за продажба'
              ].map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => { setInput(suggestion); }}
                  className="text-xs px-3 py-1.5 bg-[#1B5E37]/5 border border-[#1B5E37]/20 text-[#1B5E37] rounded-full hover:bg-[#1B5E37]/10 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-border flex-shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Напишете съобщение..."
                rows={1}
                className="flex-1 resize-none border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5E37]/30 focus:border-[#1B5E37] max-h-24"
                data-testid="ai-chat-input"
                disabled={loading}
              />
              <Button 
                size="sm"
                onClick={sendMessage} 
                disabled={!input.trim() || loading}
                className="bg-[#1B5E37] hover:bg-[#154a2c] h-10 w-10 p-0 rounded-xl"
                data-testid="ai-chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between mt-2 px-1">
              <button onClick={clearChat} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                Изчисти чата
              </button>
              <span className="text-xs text-muted-foreground">Enter за изпращане</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
